import { BOARD_ITEM_STATUSES, SUPPORT_REQUEST_STATUSES } from 'constants/statuses'

import type { SupportRequest } from 'services/supportApi'

/*
  ────────────────────────────────────────────────────────────────────────────
  THE STATUS-VOCABULARY PROBLEM, and how it is resolved (VRG-324)
  ────────────────────────────────────────────────────────────────────────────

  Company → Support now lists two lifecycles in one column:

    request     open / in_progress / waiting_on_customer / resolved / closed
    board item  under_review / planned / in_progress / shipped / declined

  So a status pill would mean different things depending on the Type badge beside
  it. That is bad on its own, but the sharp edge is that the two vocabularies
  COLLIDE on `in_progress` while meaning different things by it: "someone is
  answering your ticket" versus "we are building this feature". A reader cannot
  tell which they are looking at, and the wrong reading is the plausible one.

  THE RESOLUTION, in one sentence: a row shows the status of the lifecycle that is
  actually GOVERNING it — a promoted feature shows its BOARD status, everything
  else shows its REQUEST status — decided in exactly one place, `displayStatusKey`.

  Board keys are NAMESPACED with a `board:` prefix in the filter/sort model.
  Without it the two `in_progress` entries collapse into one indistinguishable
  filter option that silently selects rows from both vocabularies. With it they are
  two options that do not even read alike, because the board map labels its
  in_progress "Building". The user sees five request pills and five board pills,
  each in its own colours, never the same word twice.

  WHERE IT APPLIES: the CUSTOMER surfaces — the list and the request detail page,
  which must agree with each other. NOT the internal queue: a Vera staffer acts on
  the request status (mark resolved, waiting on customer), so replacing it there
  would hide the field they are there to change. That is why `RequestThread` takes
  `preferBoardStatus` rather than applying this unconditionally.

  Rejected alternatives:
    - Two facets (Status + Board status). Two controls for one column, and a row
      only ever has one of them, so every selection in one excludes every row the
      other could match.
    - Both pills on a promoted row. One of the two is stale by construction — a
      promoted request's own status stops being maintained — so the row would read
      "open" and "shipped" at once.
    - Mapping board statuses onto request statuses. Lossy exactly where it matters:
      `planned` and `under_review` both collapse to "open", erasing the only
      distinction the submitter cares about.
*/

/** Namespace for board statuses inside the shared filter/sort model. */
export const BOARD_STATUS_PREFIX = 'board:'

/**
 * Every status a customer-facing support row can DISPLAY, in one map.
 *
 * `merged` is excluded on purpose: `boardItemStatus` is read through the ACTIVE
 * item, so a merged item's status can never reach a row — the survivor's does.
 * Including it would put an option in the filter drawer that matches nothing.
 */
export const SUPPORT_DISPLAY_STATUSES: Record<
  string,
  { label: string; color: string; borderColor?: string; backgroundColor?: string }
> = {
  ...SUPPORT_REQUEST_STATUSES,
  ...Object.fromEntries(
    Object.entries(BOARD_ITEM_STATUSES)
      .filter(([key]) => key !== 'merged')
      .map(([key, value]) => [`${BOARD_STATUS_PREFIX}${key}`, value]),
  ),
}

/**
 * THE one place that decides which lifecycle a row is showing.
 *
 * Read by the card, the request detail page, the filter and the sort comparator.
 * If any of them derived it separately, a filter selection would stop matching the
 * pills on screen — which looks like a broken filter and is impossible to diagnose
 * from the UI.
 */
export const displayStatusKey = (
  request: Pick<SupportRequest, 'type' | 'status' | 'boardItemStatus'>,
): string =>
  request.type === 'feature' && request.boardItemStatus
    ? `${BOARD_STATUS_PREFIX}${request.boardItemStatus}`
    : request.status
